export type Rect = { x: number; y: number; width: number; height: number };
export type CalloutSide = 'left' | 'right' | 'above' | 'below';
export type CollisionItem = {
  id: string;
  badge: Rect;
  label?: { width: number; height: number };
  preferred?: CalloutSide;
  /** Origin and destination labels stay visible and reserve space before other labels. */
  required?: boolean;
};
export type CollisionPlacement = Rect & { id: string; side: CalloutSide; unresolved: boolean; hidden: boolean };
const GAP = 8;

export const overlaps = (a: Rect, b: Rect, gap = GAP) =>
  a.x < b.x + b.width + gap - 0.1 &&
  a.x + a.width + gap > b.x + 0.1 &&
  a.y < b.y + b.height + gap - 0.1 &&
  a.y + a.height + gap > b.y + 0.1;

/** Place endpoints first; suppress optional labels when no adjacent direction fits. */
export function placeCallouts(items: CollisionItem[], viewport: Rect, hideCrowded = true): CollisionPlacement[] {
  const obstacles = items.map(item => item.badge);
  const placed: CollisionPlacement[] = [];
  const fits = (rect: Rect) =>
    rect.x >= viewport.x &&
    rect.y >= viewport.y &&
    rect.x + rect.width <= viewport.x + viewport.width &&
    rect.y + rect.height <= viewport.y + viewport.height &&
    !obstacles.some(badge => overlaps(rect, badge)) &&
    !placed.some(label => !label.hidden && overlaps(rect, label));
  const ordered = [...items.filter(item => item.required), ...items.filter(item => !item.required)];
  for (const item of ordered) {
    if (!item.label) {
      continue;
    }
    const { badge: b, label: size } = item;
    const cx = b.x + b.width / 2;
    const cy = b.y + b.height / 2;
    const candidates: Record<CalloutSide, Rect> = {
      right: { x: b.x + b.width + GAP, y: cy - size.height / 2, ...size },
      left: { x: b.x - size.width - GAP, y: cy - size.height / 2, ...size },
      above: { x: cx - size.width / 2, y: b.y - size.height - GAP, ...size },
      below: { x: cx - size.width / 2, y: b.y + b.height + GAP, ...size },
    };
    const order = [...new Set([item.preferred ?? 'right', 'right', 'left', 'above', 'below'] as const)];
    const side = order.find(side => fits(candidates[side]));
    if (side) {
      placed.push({ id: item.id, ...candidates[side], side, unresolved: false, hidden: false });
      continue;
    }
    // Keep required endpoint labels in the viewport where possible, even when badges are crowded.
    if (item.required) {
      for (const side of order) {
        candidates[side] = {
          ...candidates[side],
          x: Math.max(viewport.x, Math.min(candidates[side].x, viewport.x + viewport.width - size.width)),
          y: Math.max(viewport.y, Math.min(candidates[side].y, viewport.y + viewport.height - size.height)),
        };
      }
    }
    const fallbackSide = item.required
      ? (order.find(side => !placed.some(label => !label.hidden && overlaps(candidates[side], label))) ?? order[0])
      : order[0];
    placed.push({
      id: item.id,
      ...candidates[fallbackSide],
      side: fallbackSide,
      unresolved: true,
      hidden: hideCrowded && !item.required,
    });
  }
  return placed;
}

/** Apply screen-space placement relative to the badge, independent of document direction. */
export function applyCalloutPlacement(element: HTMLElement, placement: CollisionPlacement, badge: Rect) {
  element.style.setProperty('--preview-callout-x', `${placement.x - badge.x}px`);
  element.style.setProperty('--preview-callout-y', `${placement.y - badge.y}px`);
  element.style.setProperty('--preview-callout-translate-y', '0px');
  element.dataset.collisionSide = placement.side;
  element.dataset.collisionUnresolved = String(placement.unresolved);
  const callout = element.querySelector<HTMLElement>('[data-preview-callout]');
  if (callout) {
    callout.style.visibility = placement.hidden ? 'hidden' : '';
  }
}
